import { createLogger } from '../../../common/logging';
import { Clip } from '../clipQueueSlice';
import afreecaClipProvider from './afreecaClip/afreecaClipProvider';
import streamableProvider from './streamable/streamableProvider';
import twitchClipProvider from './twitchClip/twitchClipProvider';
import twitchVodProvider from './twitchVod/twitchVodProvider';
import youtubeProvider from './youtube/youtubeProvider';
import kickClipProvider from './kickClip/kickClipProvider';
import tiktokProvider from './tiktok/tiktokProvider';
import twitterProvider from './twitter/twitterProvider';
import instagramProvider from './instagram/instagramProvider';
const logger = createLogger('CombinedClipProvider');

export interface ClipProvider {
  name: string;
  getIdFromUrl(url: string): string | undefined;
  getClipById(id: string): Promise<Clip | undefined>;
  getUrl(id: string): string | undefined;
  getEmbedUrl(id: string): string | undefined;
  getAutoplayUrl(id: string): Promise<string | undefined>;
  getFallbackM3u8Url?(id: string): string | undefined;
}

class CombinedClipProvider implements ClipProvider {
  name = 'combined';
  providers = {
    [twitchClipProvider.name]: twitchClipProvider,
    [twitchVodProvider.name]: twitchVodProvider,
    [youtubeProvider.name]: youtubeProvider,
    [streamableProvider.name]: streamableProvider,
    [afreecaClipProvider.name]: afreecaClipProvider,
    [kickClipProvider.name]: kickClipProvider,
    [tiktokProvider.name]: tiktokProvider,
    [twitterProvider.name]: twitterProvider,
    [instagramProvider.name]: instagramProvider,
  };
  // Initialize with default providers to match the default state in clipQueueSlice
  enabledProviders: string[] = ['twitch-clip', 'twitch-vod', 'kick-clip', 'youtube', 'streamable', 'tiktok', 'twitter', 'instagram'];

  getIdFromUrl(url: string): string | undefined {
    console.log('[CombinedClipProvider] getIdFromUrl called with:', url);
    console.log('[CombinedClipProvider] Enabled providers:', this.enabledProviders);

    for (const providerName of this.enabledProviders) {
      const provider = this.providers[providerName];
      console.log(`[CombinedClipProvider] Checking provider: ${providerName}`, { exists: !!provider });

      if (provider) {
        const id = provider.getIdFromUrl(url);
        console.log(`[CombinedClipProvider] Provider ${providerName} returned ID:`, id);

        if (id) {
          const combinedId = `${provider.name}:${id}`;
          console.log(`[CombinedClipProvider] Returning combined ID:`, combinedId);
          return combinedId;
        }
      }
    }

    logger.warn('No provider matched URL:', url);
    return undefined;
  }

  async getClipById(id: string): Promise<Clip | undefined> {
    const [provider, idPart] = this.getProviderAndId(id);
    const clip = await provider?.getClipById(idPart);

    if (clip) {
      clip.id = id;
    }

    return clip;
  }

  getUrl(id: string): string | undefined {
    const [provider, idPart] = this.getProviderAndId(id);
    return provider?.getUrl(idPart);
  }
  getEmbedUrl(id: string): string | undefined {
    const [provider, idPart] = this.getProviderAndId(id);
    return provider?.getEmbedUrl(idPart);
  }
  async getAutoplayUrl(id: string): Promise<string | undefined> {
    const [provider, idPart] = this.getProviderAndId(id);
    return await provider?.getAutoplayUrl(idPart);
  }
  getFallbackM3u8Url(id: string): string | undefined {
    const [provider, idPart] = this.getProviderAndId(id);
    return provider?.getFallbackM3u8Url?.(idPart);
  }

  setProviders(providers: string[]) {
    logger.info('setProviders', providers);
    this.enabledProviders = providers;
  }

  private getProviderAndId(id: string): [ClipProvider | undefined, string] {
    // Split only on the first colon to handle IDs that contain colons (e.g., twitter:username:tweetId)
    const colonIndex = id.indexOf(':');
    if (colonIndex === -1) {
      return [undefined, id];
    }

    const providerName = id.substring(0, colonIndex);
    const idPart = id.substring(colonIndex + 1);
    const provider = this.providers[providerName];

    return [provider, idPart];
  }
}

const clipProvider = new CombinedClipProvider();

export default clipProvider;
